import {
  enumKeyOf,
  enumKeys
} from "common/helpers/utils";
import Guid from "common/values/guid/guid";
import {
  AccessLevel,
  Resource, ResourceConstructor
} from "permissions/entities/permission/resources/general";
import {
  LegalEntityAccess,
  LegalEntityAccessType
} from "permissions/entities/permission/resources/legal-entities-resources";
import {
  CardType,
  GroupedCellConfig,
  MetaControlType,
  TableRow
} from "permissions/entities/permission/view/components/permissions-table/types";
import { isPermissionsRow, isTemplateRow } from "permissions/entities/permission/view/components/permissions-table/utils";
import { JSX } from "react";
import { type MRT_Row as Row } from "material-react-table";

type TopLevelTemplates = {
  byConstructor: Map<ResourceConstructor, Resource>;
  legalByAccessType: Map<string, Resource>;
  docContentsBySubject: Map<string, Resource>;
};

type LegalEntityGroupCellProps = {
  row: TableRow | null;
  topLevelTemplates: TopLevelTemplates;
  isSingleGranteeView: boolean;
  isCreateTemplateView: boolean;
  isManageTemplatesView: boolean;
  canEdit: boolean;
  creatingRow: boolean | null;
  editingRow: Row<TableRow> | null;
  resources: Resource[];
  renderGroupedPermission: (row: TableRow, config: GroupedCellConfig) => JSX.Element;
}

export default function LegalEntityGroupCell(props: Readonly<LegalEntityGroupCellProps>): JSX.Element | null {
  const {
    row,
    topLevelTemplates,
    isSingleGranteeView,
    isCreateTemplateView,
    isManageTemplatesView,
    canEdit,
    creatingRow,
    editingRow,
    resources,
    renderGroupedPermission,
  } = props;

  if (!row) return null;
  return renderGroupedPermission(row, {
    cardType: CardType.Grouped,
    keys: enumKeys(LegalEntityAccessType).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" })
    ),
    templateForKey: (atKey) => topLevelTemplates.legalByAccessType.get(atKey),
    candidatesForKey: (row, atKey) => {
      if (isPermissionsRow(row)) {
        return row.recentPermissionsPreview.filter((permission) =>
          permission.resource instanceof LegalEntityAccess &&
          enumKeyOf(LegalEntityAccessType, permission.resource.accessType) === atKey
        );
      }
      if (isTemplateRow(row)) {
         let source: Resource[];
        const isCreatingThisRow = creatingRow && row.id?.isEqualTo?.(Guid.empty);
        const isEditingThisRow =
          Boolean(editingRow?.original) && editingRow!.original.id?.isEqualTo?.(row.id);

        if (isEditingThisRow || isCreatingThisRow) {
          source = resources;
        } else {
          source = row.resources;
        }
        
        return source.filter((resource) =>
          resource instanceof LegalEntityAccess &&
          enumKeyOf(LegalEntityAccessType, resource.accessType) === atKey
        );
      }
      return [];
    },
    labelForKey: (atKey) => atKey,
    controlMetaForResource: (resource) => ({
      type: MetaControlType.AccessLevel,
      label: "Access Level",
      options: enumKeys(AccessLevel),
      value: enumKeyOf(AccessLevel, (resource as LegalEntityAccess).accessLevel),
    }),
    title: (isSingleGranteeView || isCreateTemplateView || isManageTemplatesView) 
      ? "Legal Entity" : undefined,
    colSpan: 2,
    rowSpan: 2,
    canEdit
  });
}
